/*
 * Look of an HMI project: colour scheme and corners.
 *
 * Set by the author in the editor and saved with the project, like the
 * arrangement of the widgets. Viewers only switch light/dark (the app's
 * theme), so every scheme has a light and a dark variant.
 *
 * Pure module (no runtime imports), so the tests can load it without a
 * bundler. All styling is token overrides in styles/tokens.css, keyed on the
 * attributes from `hmiAppearanceAttributes`; components only use tokens.
 *
 * Adding a palette:
 *   1. an entry in PALETTE_OPTIONS below,
 *   2. a light and a dark block in styles/tokens.css,
 *   3. a value in the C# enum `HmiPalette` (HmiProject.cs), else the server
 *      rejects projects that use it.
 * `tests/hmiPaletteTokens.test.ts` fails if 1 and 2 disagree.
 */

import type { HmiTheme } from './themeModel';

export type HmiPalette = 'standard' | 'clear' | 'technical';
export type HmiCorners = 'round' | 'square';

export interface HmiAppearance {
  palette: HmiPalette;
  corners: HmiCorners;
  /** Theme (docs/hmi-panel-ausbau.md §3). Read by projectApi with
   *  `parseTheme`; resolveAppearance below leaves it out on purpose, it
   *  only decides palette and corners. */
  theme?: HmiTheme;
}

/** A project without an appearance looks as HMI projects always did. */
export const DEFAULT_APPEARANCE: Readonly<HmiAppearance> = { palette: 'standard', corners: 'round' };

export interface AppearanceOption<T extends string> {
  value: T;
  label: string;
  hint: string;
}

export const PALETTE_OPTIONS: readonly AppearanceOption<HmiPalette>[] = [
  { value: 'standard', label: 'Standard', hint: 'Wie gewohnt' },
  { value: 'clear', label: 'Klar', hint: 'Ruhig, viel Weißraum' },
  { value: 'technical', label: 'Technik', hint: 'Kontrastreich, Messwerte in Cyan' },
];

export const CORNER_OPTIONS: readonly AppearanceOption<HmiCorners>[] = [
  { value: 'round', label: 'Rund', hint: 'Abgerundete Ecken' },
  { value: 'square', label: 'Eckig', hint: 'Gerade Kanten' },
];

/** Value from a project body to a known one. Anything unknown (written by
 *  a newer client, or by hand) falls back to the default instead of
 *  breaking the screen. */
export function parsePalette(raw: unknown): HmiPalette {
  return PALETTE_OPTIONS.some((o) => o.value === raw) ? (raw as HmiPalette) : DEFAULT_APPEARANCE.palette;
}

export function parseCorners(raw: unknown): HmiCorners {
  return CORNER_OPTIONS.some((o) => o.value === raw) ? (raw as HmiCorners) : DEFAULT_APPEARANCE.corners;
}

/** The `appearance` field of a project body, tolerant of missing or
 *  partial values. */
export function resolveAppearance(raw: unknown): HmiAppearance {
  const obj = raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return { palette: parsePalette(obj.palette), corners: parseCorners(obj.corners) };
}

/** Attributes to spread onto the element that frames a project's widgets
 *  (live grid, editor canvas, preview stage). Takes the raw `appearance`
 *  value of the project. The default produces attributes too, but no
 *  stylesheet rule matches them, so it looks exactly as before. */
export function hmiAppearanceAttributes(raw: unknown): Record<'data-hmi-palette' | 'data-hmi-corners', string> {
  const a = resolveAppearance(raw);
  return { 'data-hmi-palette': a.palette, 'data-hmi-corners': a.corners };
}
